import type { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/admin/coaching/coaching-dashboard/shared/tooltip'
import {
  isPastWeek,
  type TrayCard,
  unpublishBlockedReason,
  type Week,
} from '../_lib/auto-place'
import { formatHoursDuration } from '../_lib/duration'
import { DayColumn } from './day-column'
import type { ScheduleCardActions } from './schedule-card'

export interface WeekViewProps {
  week: Week
  usedHoursByDay: readonly number[]
  placing: TrayCard | null
  deleteConfirmationOpen: boolean
  onToggle: () => void
  /**
   * Folds this elapsed week away behind the schedule's "show N earlier week(s)"
   * disclosure, or brings it back. Omitted when the board cannot persist the
   * choice (mock mode) — see `CurriculumSlice.toggleWeekFold`.
   */
  onToggleFold?: () => void
  onPublish: () => void
  onUnpublish: () => void
  onRequestDelete: () => void
  onCancelDelete: () => void
  onDelete: () => void
  onSetBudget: (dayIdx: number, budget: number) => void
  onPlace: (dayIdx: number) => void
  onOpenCustom: (dayIdx: number) => void
  cardActions: ScheduleCardActions
  publishedReadOnly?: boolean
}

const statusMeta = (
  status: Week['status'],
): { label: string; color: string; background: string } => {
  if (status === 'published') {
    return {
      label: 'PUBLISHED · client sees this',
      color: 'var(--cd-green)',
      background: 'color-mix(in oklab, var(--cd-green) 10%, transparent)',
    }
  }
  if (status === 'draft') {
    return {
      label: 'DRAFT · hidden from client',
      color: 'var(--cd-amber)',
      background: 'color-mix(in oklab, var(--cd-amber) 10%, transparent)',
    }
  }
  return {
    label: 'PAST',
    color: 'var(--cd-text-faint)',
    background: 'var(--cd-inset)',
  }
}

export function WeekView(props: WeekViewProps): ReactNode {
  const {
    week,
    usedHoursByDay,
    placing,
    deleteConfirmationOpen,
    onToggle,
    onToggleFold,
    onPublish,
    onUnpublish,
    onRequestDelete,
    onCancelDelete,
    onDelete,
    onSetBudget,
    onPlace,
    onOpenCustom,
    cardActions,
    publishedReadOnly = false,
  } = props
  const status = statusMeta(week.status)
  const isDraft = week.status === 'draft'
  // A week the board shows as `past` is never offered the action, whatever
  // the server stored: an elapsed week is not one the coach can edit after
  // taking it back, which is the only reason to take it back.
  const isPublished = week.status === 'published'
  const unpublishBlocked = isPublished ? unpublishBlockedReason(week) : null
  const readOnly =
    isPastWeek(week) || (publishedReadOnly && week.status === 'published')
  const taskCount = week.days.reduce(
    (total, day) =>
      total + day.cards.filter((card) => card.status !== 'cancelled').length,
    0,
  )
  const plannedHours = usedHoursByDay.reduce((total, hours) => total + hours, 0)

  return (
    <section
      aria-label={week.label}
      className="overflow-hidden rounded-xl border"
      style={{
        borderColor: 'var(--cd-border)',
        borderStyle: isDraft ? 'dashed' : 'solid',
      }}
    >
      <div className="flex flex-wrap items-center gap-2 bg-(--cd-inset) px-3 py-2">
        <button
          type="button"
          aria-label={`${week.open ? 'Collapse' : 'Expand'} ${week.label}`}
          aria-expanded={week.open}
          onClick={onToggle}
          className="px-0.5 text-[11px] text-(--cd-text-muted)"
        >
          {week.open ? '▾' : '▸'}
        </button>
        <h3 className="text-[12.5px] font-bold text-(--cd-text)">
          {week.label}
          {week.sprint ? (
            <span className="font-semibold text-(--cd-text-muted)">
              {' '}
              · {week.sprint.name}
            </span>
          ) : null}
        </h3>
        <span
          className="rounded-full px-2 py-0.5 text-[9px] font-bold"
          style={{ color: status.color, background: status.background }}
        >
          {status.label}
        </span>
        <span className="text-[10.5px] text-(--cd-text-faint)">
          {taskCount} tasks · {formatHoursDuration(plannedHours)} planned
        </span>

        {isDraft ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {deleteConfirmationOpen ? (
              <>
                <span className="text-[10.5px] font-semibold text-(--cd-red)">
                  Delete week —
                </span>
                <button
                  type="button"
                  onClick={onDelete}
                  className="rounded-full border border-(--cd-border-2) bg-(--cd-tab-active) px-2.5 py-0.5 text-[10.5px] font-semibold text-(--cd-text)"
                >
                  cards → tray
                </button>
                <button
                  type="button"
                  onClick={onCancelDelete}
                  className="text-[10.5px] text-(--cd-text-muted)"
                >
                  cancel
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  aria-label={`Publish ${week.label}`}
                  onClick={onPublish}
                  className="rounded-full border border-(--cd-green)/35 px-2.5 py-0.5 text-[10.5px] font-semibold text-(--cd-green)"
                >
                  Publish week
                </button>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Delete ${week.label}`}
                      onClick={onRequestDelete}
                      className="text-[11px] text-(--cd-text-muted) hover:text-(--cd-red)"
                    >
                      ✕
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Delete week</TooltipContent>
                </Tooltip>
              </>
            )}
          </div>
        ) : null}

        {isPublished ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                {/* `aria-disabled`, not `disabled`: a disabled button leaves
                    the tab order and fires no pointer events, so the reason
                    this one is refused would be unreachable by keyboard, by
                    touch, and — since browsers suppress hover on it too — by
                    mouse. Both boards re-check the reason on click and answer
                    with it, so the press is refused, not obeyed. */}
                <button
                  type="button"
                  aria-label={`Unpublish ${week.label}`}
                  aria-disabled={unpublishBlocked !== null}
                  onClick={onUnpublish}
                  className="rounded-full border border-(--cd-border-2) px-2.5 py-0.5 text-[10.5px] font-semibold text-(--cd-text-muted) hover:text-(--cd-text) aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                >
                  Unpublish week
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {unpublishBlocked ??
                  'Take this week back to draft so it can be edited'}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : null}

        {week.elapsed && onToggleFold ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  // Named for the week it acts on, like every other week
                  // control: a row of bare "Fold away" buttons is unusable by
                  // name alone once more than one past week is on the board.
                  aria-label={`${week.folded ? 'Unfold' : 'Fold'} ${week.label}`}
                  onClick={onToggleFold}
                  className="rounded-full border border-(--cd-border-2) px-2.5 py-0.5 text-[10.5px] font-semibold text-(--cd-text-muted) hover:text-(--cd-text)"
                >
                  {week.folded ? 'Unfold week' : 'Fold week'}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {week.folded
                  ? 'Keep this week on the board'
                  : 'Tuck this week behind "show earlier week(s)"'}
              </TooltipContent>
            </Tooltip>
          </div>
        ) : null}
      </div>
      {week.sprint?.inFlight ? (
        // Read-only. The week is editable on the same terms as any other
        // published or draft week; the line only says what the edit reaches.
        <p
          role="note"
          className="border-t px-3 py-1.5 text-[10.5px] font-semibold"
          style={{
            borderColor: 'var(--cd-border)',
            color: 'var(--cd-amber)',
            background: 'color-mix(in oklab, var(--cd-amber) 8%, transparent)',
          }}
        >
          This client is locked into {week.sprint.name}. Changes here reach a
          paid week, so make them deliberately.
          {week.sprint.reducedRequirements &&
            ' Reduced requirements: an admin has said less is expected this week.'}
        </p>
      ) : null}

      {week.open ? (
        <div className="grid min-w-[920px] grid-cols-7 gap-px bg-(--cd-border)">
          {week.days.map((day, dayIdx) => (
            <DayColumn
              key={day.label}
              day={day}
              weekId={week.id}
              dayIdx={dayIdx}
              usedHours={usedHoursByDay[dayIdx] ?? 0}
              readOnly={readOnly}
              // Pass the armed card even for read-only days: all placement
              // actions stay gated on `!readOnly` inside DayColumn, but the day
              // still needs to know a placement is in flight so it can surface
              // the "locked — pick a draft week" hint instead of going silent.
              placing={placing}
              onSetBudget={(budget) => onSetBudget(dayIdx, budget)}
              onPlace={() => onPlace(dayIdx)}
              onOpenCustom={() => onOpenCustom(dayIdx)}
              cardActions={cardActions}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
